import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { projectsAPI, catalogAPI, progressAPI, evidenceAPI, usersAPI, reportsAPI } from '../services/api';
import { 
  Building2, 
  MapPin, 
  Calendar, 
  DollarSign,
  Users,
  FileSpreadsheet,
  TrendingUp,
  Image,
  Download,
  Plus,
  ArrowLeft,
  Pencil,
  UserPlus,
  Check,
  X,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDeveloper, isConstructor, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [progress, setProgress] = useState([]);
  const [evidences, setEvidences] = useState([]);
  const [constructors, setConstructors] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedConstructor, setSelectedConstructor] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, catalogRes, progressRes, evidencesRes] = await Promise.all([
        projectsAPI.getById(id),
        catalogAPI.getByProject(id),
        progressAPI.getByProject(id),
        evidenceAPI.getByProject(id)
      ]);
      
      setProject(projectRes?.data?.data || projectRes?.data);
      
      const catalogData = catalogRes?.data?.data || catalogRes?.data;
      setCatalog(Array.isArray(catalogData) ? catalogData : []);
      
      const progressData = progressRes?.data?.data || progressRes?.data;
      setProgress(Array.isArray(progressData) ? progressData : []);
      
      const evidencesData = evidencesRes?.data?.data || evidencesRes?.data;
      setEvidences(Array.isArray(evidencesData) ? evidencesData : []);

      if (isDeveloper()) {
        const constructorsRes = await usersAPI.getConstructors();
        const constructors = constructorsRes?.data?.data || constructorsRes?.data || [];
        setConstructors(constructors);
      }
    } catch (error) {
      console.error('Error al cargar datos del proyecto:', error);
      toast.error('Error al cargar datos del proyecto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignConstructor = async () => {
    if (!selectedConstructor) return;
    
    try {
      await projectsAPI.assignConstructor(id, selectedConstructor);
      toast.success('Constructor asignado correctamente');
      setAssignDialogOpen(false);
      setSelectedConstructor('');
      fetchData();
    } catch (error) {
      toast.error('Error al asignar constructor');
    }
  };

  const handleApproveProgress = async (progressId) => {
    try {
      await progressAPI.approve(progressId);
      toast.success('Avance aprobado');
      fetchData();
    } catch (error) {
      toast.error('Error al aprobar avance');
    }
  };

  const handleRejectProgress = async (progressId) => {
    try {
      await progressAPI.reject(progressId);
      toast.success('Avance rechazado');
      fetchData();
    } catch (error) {
      toast.error('Error al rechazar avance');
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await reportsAPI.downloadProjectExcel(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${project.name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al descargar reporte');
    }
  };

  const downloadPDF = async () => {
    try {
      const summaryRes = await reportsAPI.getProjectSummary(id);
      const summary = summaryRes?.data?.data || summaryRes?.data;
      
      const doc = new jsPDF();
      
      // Header
      doc.setFillColor(192, 86, 33);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text('BROCO', 20, 25);
      doc.setFontSize(12);
      doc.text('Reporte de Obra', 20, 35);
      
      // Project Info
      doc.setTextColor(45, 42, 38);
      doc.setFontSize(18);
      doc.text(summary.project.name, 20, 55);
      doc.setFontSize(10);
      doc.setTextColor(92, 85, 75);
      doc.text(`Ubicación: ${summary.project.location || 'No especificada'}`, 20, 63);
      doc.text(`Estado: ${summary.project.status}`, 20, 70);
      
      // Budget Section
      doc.setFontSize(14);
      doc.setTextColor(45, 42, 38);
      doc.text('Presupuesto', 20, 85);
      
      doc.autoTable({
        startY: 90,
        head: [['Concepto', 'Monto']],
        body: [
          ['Total', `$${summary.budget.total.toLocaleString()}`],
          ['Ejecutado', `$${summary.budget.executed.toLocaleString()}`],
          ['Restante', `$${summary.budget.remaining.toLocaleString()}`],
          ['Avance', `${summary.budget.percentage}%`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [192, 86, 33] }
      });
      
      // Progress Stats
      const finalY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.text('Estadísticas de Avances', 20, finalY);
      
      doc.autoTable({
        startY: finalY + 5,
        head: [['Estado', 'Cantidad']],
        body: [
          ['Total', summary.progress.total],
          ['Pendientes', summary.progress.pending],
          ['Aprobados', summary.progress.approved],
          ['Rechazados', summary.progress.rejected]
        ],
        theme: 'grid',
        headStyles: { fillColor: [129, 161, 131] }
      });
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(`Generado: ${new Date().toLocaleString('es-MX')}`, 20, 280);
      
      doc.save(`reporte_${project.name}.pdf`);
      toast.success('PDF generado');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      activo: 'bg-[#C05621]/10 text-[#C05621]',
      completado: 'bg-green-100 text-green-800',
      pausado: 'bg-amber-100 text-amber-800',
      pendiente: 'bg-amber-100 text-amber-800',
      aprobado: 'bg-green-100 text-green-800',
      rechazado: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.activo;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="project-detail">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <Link to="/projects" className="inline-flex items-center gap-2 text-[#5C554B] hover:text-[#C05621] transition-colors w-fit">
            <ArrowLeft className="w-4 h-4" />
            Volver a obras
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#C05621]/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-[#C05621]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">{project.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className={getStatusBadge(project.status)}>{project.status}</Badge>
                  {project.location && (
                    <span className="flex items-center gap-1 text-sm text-[#5C554B]">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              {isDeveloper() && (
                <>
                  <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="btn-secondary" data-testid="assign-btn">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Asignar
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Asignar Constructor</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <Select value={selectedConstructor} onValueChange={setSelectedConstructor}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Seleccionar constructor" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(constructors) && constructors.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} - {c.company_name || c.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button onClick={handleAssignConstructor} className="btn-primary w-full">
                          Asignar
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  <Link to={`/projects/${id}/edit`}>
                    <Button variant="outline" className="btn-secondary">
                      <Pencil className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  </Link>
                </>
              )}
              
              <Button onClick={downloadExcel} variant="outline" className="btn-secondary">
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button onClick={downloadPDF} className="btn-primary">
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C05621]/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#C05621]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C554B]">Presupuesto</p>
                  <p className="font-bold text-[#2D2A26]">{formatCurrency(project.budget)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#81A183]/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#81A183]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C554B]">Ejecutado</p>
                  <p className="font-bold text-[#2D2A26]">{formatCurrency(project.executed_budget)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C554B]">Partidas</p>
                  <p className="font-bold text-[#2D2A26]">{Array.isArray(catalog) ? catalog.length : 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C554B]">Contratistas</p>
                  <p className="font-bold text-[#2D2A26]">{project.assigned_constructors?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-[#2D2A26]">Avance General</span>
              <span className="text-sm font-bold text-[#C05621]">{project.progress_percentage || 0}%</span>
            </div>
            <Progress value={project.progress_percentage || 0} className="h-3" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="catalog" className="space-y-4">
          <TabsList className="bg-[#EBE5DA]">
            <TabsTrigger value="catalog" className="data-[state=active]:bg-white">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Avances
            </TabsTrigger>
            <TabsTrigger value="evidences" className="data-[state=active]:bg-white">
              <Image className="w-4 h-4 mr-2" />
              Evidencias
            </TabsTrigger>
          </TabsList>

          <TabsContent value="catalog">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Catálogo de Partidas</CardTitle>
                {isDeveloper() && (
                  <Link to={`/catalogs?project=${id}`}>
                    <Button size="sm" className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar
                    </Button>
                  </Link>
                )}
              </CardHeader>
              <CardContent>
                {!Array.isArray(catalog) || catalog.length === 0 ? (
                  <div className="text-center py-8">
                    <FileSpreadsheet className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                    <p className="text-[#5C554B]">No hay partidas en el catálogo</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Código</TableHead>
                          <TableHead>Descripción</TableHead>
                          <TableHead className="text-right">Cantidad</TableHead>
                          <TableHead className="text-right">P.U.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Ejecutado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Array.isArray(catalog) && catalog.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm">{item.code}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total_price)}</TableCell>
                            <TableCell className="text-right">
                              <span className={item.executed_quantity > 0 ? 'text-[#81A183]' : 'text-[#9CA3AF]'}>
                                {item.executed_quantity} {item.unit}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="progress">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Avances de Obra</CardTitle>
                {isConstructor() && (
                  <Link to={`/progress?project=${id}`}>
                    <Button size="sm" className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Capturar
                    </Button>
                  </Link>
                )}
              </CardHeader>
              <CardContent>
                {!Array.isArray(progress) || progress.length === 0 ? (
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                    <p className="text-[#5C554B]">No hay avances registrados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Array.isArray(progress) && progress.map((p) => {
                      const catalogItem = catalog.find(c => c.id === p.catalog_item_id);
                      return (
                        <div key={p.id} className="flex items-center justify-between p-4 rounded-xl border border-[#E2E8F0]">
                          <div className="flex-1">
                            <p className="font-medium text-[#2D2A26]">
                              {catalogItem?.code || 'Partida'} - {p.quantity} {catalogItem?.unit || 'unidades'}
                            </p>
                            <p className="text-sm text-[#5C554B]">{p.notes || 'Sin notas'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-[#9CA3AF]" />
                              <span className="text-xs text-[#9CA3AF]">
                                {new Date(p.created_at).toLocaleDateString('es-MX')}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusBadge(p.status)}>{p.status}</Badge>
                            {isDeveloper() && p.status === 'pendiente' && (
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleApproveProgress(p.id)}
                                  className="h-8 w-8 text-green-600 hover:bg-green-50"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleRejectProgress(p.id)}
                                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="evidences">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Evidencias</CardTitle>
                <Link to={`/evidences?project=${id}`}>
                  <Button size="sm" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Subir
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {!Array.isArray(evidences) || evidences.length === 0 ? (
                  <div className="text-center py-8">
                    <Image className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                    <p className="text-[#5C554B]">No hay evidencias subidas</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.isArray(evidences) && evidences.map((evidence) => (
                      <a
                        key={evidence.id}
                        href={evidenceAPI.getFileUrl(evidence.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative aspect-square rounded-xl overflow-hidden border border-[#E2E8F0] hover:border-[#C05621] transition-colors"
                      >
                        {evidence.evidence_type === 'photo' ? (
                          <img
                            src={evidenceAPI.getFileUrl(evidence.id)}
                            alt={evidence.description || 'Evidencia'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#FDFBF7]">
                            <FileSpreadsheet className="w-8 h-8 text-[#C05621] mb-2" />
                            <span className="text-xs text-[#5C554B] text-center px-2 truncate w-full">
                              {evidence.file_name}
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                          <Badge className="text-xs bg-white/90 text-[#2D2A26]">
                            {evidence.evidence_type}
                          </Badge>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ProjectDetail;
